import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { useCallback } from 'react';

import { STANDARD_FIELD_HEIGHT } from '../../configs/ui-constants';

export interface HomeMatrixToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  rowCount: number;
  allCollapsed: boolean;
  onToggleAll: () => void;
}

const TOOLBAR_BUTTON_SX = {
  height: STANDARD_FIELD_HEIGHT,
  px: '10px',
  border: 1,
  borderColor: 'v4.borderDark',
  borderRadius: 1,
  bgcolor: 'transparent',
  font: 'inherit',
  fontSize: '11px',
  fontWeight: 600,
  color: 'text.secondary',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'v4.tableHover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
};

export const HomeMatrixToolbar: React.FC<HomeMatrixToolbarProps> = ({
  search,
  onSearchChange,
  rowCount,
  allCollapsed,
  onToggleAll,
}) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value),
    [onSearchChange]
  );

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        component="span"
        sx={{ fontSize: '11px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
      >
        {rowCount.toLocaleString()} {rowCount === 1 ? 'row' : 'rows'}
      </Box>

      <TextField
        value={search}
        onChange={handleChange}
        placeholder="Search"
        sx={{ width: 180, '& .MuiOutlinedInput-root': { height: STANDARD_FIELD_HEIGHT } }}
        // MUI v7 slot API — `inputProps`/`InputProps` are deprecated.
        slotProps={{
          htmlInput: { 'aria-label': 'Search clients and job types' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14 }} />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box component="button" type="button" onClick={onToggleAll} sx={TOOLBAR_BUTTON_SX}>
        {allCollapsed ? 'Expand all' : 'Collapse all'}
      </Box>
    </Stack>
  );
};
